import React, { useCallback, useMemo, useEffect } from 'react';
import { useDocumentState } from '../../../contexts/DocumentContext';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { DocumentBadgeContainer } from '../../../components/document-wizard/document-badge-container/DocumentBadgeContainer';
import {
  DocumentPermissions,
  useDocumentTeamPermissions,
} from '../shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { DocumentWizardHeaderContainer } from '../../../components/document-wizard/document-wizard-header/DocumentWizardHeaderContainer';
import { DocumentWizardHeaderLastSaved } from '../../../components/document-wizard/document-wizard-header/document-wizard-header-lastsaved/DocumentWizardHeaderLastSaved';
import { MobileApprovalHeader } from '../../../components/document-wizard/document-wizard-header/MobileApprovalHeader';
import { MobileApprovalActionsDropdown } from '../../../components/document-wizard/document-wizard-header/components/headerActions/MobileApprovalActionsDropdown';
import { MobileDocumentHeader } from '../../../components/document-wizard/document-wizard-header/MobileDocumentHeader';
import { MobileCreatorPendingHeader } from '../../../components/document-wizard/document-wizard-header/MobileCreatorPendingHeader';
import RequestReviewContainer from '../../../components/document-wizard/document-wizard-header/components/headerActions/requestReview/RequestReviewContainer';
import { useIsMobile } from '../../../shared/hooks/useIsMobile';
import LeaseWizardSideNav from '../../../components/document-wizard/document-wizard-sidenav/documentWizardSideNav';
import { CommentContextProvider } from '../../../contexts/commentContext';
import { DocumentEditorSwitch } from '../../../components/document-wizard/document-editor/DocumentEditorSwitch';
import { CompareRedlineView } from '../../../components/document-wizard/document-editor/docx-native/CompareRedlineView';
import { DealVersionViewer } from '../../../components/document-wizard/document-editor/docx-native/DealVersionViewer';
import { AwaitingCounterpartyView } from '../../../components/document-wizard/document-editor/docx-native/AwaitingCounterpartyView';
import { buildDealCompareVersions } from '../../../components/document-wizard/document-editor/docx-native/dealCompareVersions';
import { SignersSidebar } from '../../../components/document-wizard/document-editor/components/signersSidebar';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import {
  DocumentSourceEnum,
  givePossessionSuccessMessage,
  successSendDocumentMessage,
} from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import { UploadedDocumentsViewer } from '../../uploadedWebViewer/uploadedViewer';
import { DocumentSubheaderContainer } from '../../../components/document-wizard/document-wizard-header/DocumentSubheaderContainer';
import '../shared/document-wizard-page.scss';
import { ApprovalRequestStatus } from '../../../components/document-wizard/document-wizard-header/components/headerActions/requestReview/RequestInternalApprovalModalConstants';
import DocumentActivitiesAndTemplatesSidenav from '../../../components/document-wizard/documentActivitiesAndTemplatesNavigation/documentActivitiesAndTemplatesSidenav';
import { useModals } from '../../../contexts/ModalsContext';
import { givePossessionToCounterparty } from '../../../utils/api/give-possession-to-counterparty';
import { saveDocumentVersionOnPossessionPass } from '../../../components/document-wizard/document-wizard-header/document-wizard-header.utils';
import { fillPlaceholders, paths } from '../../../routing';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth';
import { useDeal } from '../../../contexts/DealContext';
import { useIntl } from 'react-intl';
import { useNotifications } from '../../../notifications';
import { getDocumentById } from '../../../api/queries_v2';
import { queriesV2, mutations } from '../../../api';
import useDocumentRevisionHistory from '../../../utils/api/document-revisions/useDocumentRevisionHistory';
import { useSendDropdownLogic } from '../../../shared/hooks/useSendDropdownLogic/useSendDropdownLogic';
import { AiChatSlideout } from '../../../components/ai-chat/AiChatSlideout';
import { ExecutePageActions } from '../../../components/document-wizard/document-wizard-header/components/ExecutePageActions/ExecutePageActions';
import { unsendDocument } from '../../../utils/api/unsend-document-offer/unsend-document';
import ConfirmActionModal from '../../../components/confirm-action-modal';
import { FormattedMessage } from 'react-intl';

export interface DocumentPageProps {
  canEdit: boolean;
  canComment: boolean;
  onSave: any;
  onTitleSave: (title: string) => void;
  lastUpdated: string;
  documentTitle: string;
  isDocumentCreator: boolean;
  hasCurrentTeamPossession: boolean;
  document: any;
  documentId: string;
  teamId: any;
  isShared?: boolean;
  canInvite: boolean;
  permissions: DocumentPermissions;
  documentStatus: DocumentStatusEnum;
  handleClose: () => void;
  dealId: string;
  documentSource: DocumentSourceEnum;
  isDocXDocument: boolean;
}

const DocumentPage = ({
  canEdit,
  canComment,
  onSave,
  onTitleSave,
  lastUpdated,
  documentTitle,
  isDocumentCreator,
  hasCurrentTeamPossession,
  document,
  documentId,
  teamId,
  canInvite,
  permissions,
  isShared,
  documentStatus,
  handleClose,
  dealId,
  documentSource,
  isDocXDocument,
}: DocumentPageProps) => {
  const apolloClient = useApolloClient();
  const { state, stateRef, updateDocumentState } = useDocumentState();
  const navigate = useNavigate();
  const [{ user }] = useAuth();
  const isDocumentLoaded = state.isEditorLoaded;
  const isDocumentRestricted = state.restrictedEditingMode;
  const approvalRequest = state.approvalRequest;
  const { showHeaderFooterEditorModal: isHeaderFooterEditorModalOpen, setShowHeaderFooterEditorModal } = useModals();
  const isEditable = state.isEditable;
  const isCompareModeEnabled = state.isCompareModeEnabled;
  const [docxViewCompare, setDocxViewCompare] = React.useState(false);
  // Autosave updates the body via GraphQL but does NOT refresh the cached
  // `lastUpdated` from the document query, so the header clock would sit frozen
  // while saves are actually landing. Stamp it locally on each successful save.
  const [lastSavedLocal, setLastSavedLocal] = React.useState<string | null>(null);
  const handleDocxSaved = useCallback(() => setLastSavedLocal(new Date().toISOString()), []);
  const isRequestReviewApproved = approvalRequest?.status === ApprovalRequestStatus.APPROVED;
  const isRequestReviewPending = approvalRequest?.status === ApprovalRequestStatus.PENDING;

  // Check current user role in approval request
  const userIsApprover =
    approvalRequest?.reviewers?.some((reviewer: { id: string }) => reviewer.id === user?.id) ?? false;
  const userIsCreator =
    approvalRequest?.creator && typeof approvalRequest.creator === 'object' && 'id' in approvalRequest.creator
      ? approvalRequest.creator.id === user?.id
      : false;
  const optionalMessageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [isUnsendModalOpen, setIsUnsendModalOpen] = React.useState(false);
  const [isUnsendLoading, setIsUnsendLoading] = React.useState(false);

  // Smart approval mode detection
  const isMobile = useIsMobile();
  const isMobileApprovalMode = isMobile && isRequestReviewPending && userIsApprover;
  const isMobileCreatorPendingMode = isMobile && isRequestReviewPending && userIsCreator;

  // Normal mobile mode (not approval, not creator pending)
  const isMobileNormalMode = isMobile && !isMobileApprovalMode && !isMobileCreatorPendingMode;
  
  // Check if document is in signing state
  const isSigning = documentStatus === DocumentStatusEnum.SIGNING;

  // General approval mode: Clean read-only interface when approval is pending,
  // UNLESS permitEditingDuringApproval is true AND user is creator or approver
  const isApprovalMode =
    isRequestReviewPending && !(approvalRequest?.permitEditingDuringApproval && (userIsApprover || userIsCreator));

  // Editor permissions - restrict editing during approval mode, but allow comments
  const editorPermissions = isApprovalMode
    ? { ...permissions, hasEditPermission: false, hasCommentPermission: true }
    : permissions;

  // Legacy mobile permissions for backwards compatibility
  const mobileEditorPermissions = isMobileApprovalMode
    ? { ...editorPermissions, hasEditPermission: false, hasCommentPermission: true }
    : editorPermissions;

  // Enable pinch-to-zoom on mobile approval mode
  React.useEffect(() => {
    if (isMobileApprovalMode) {
      const viewport = window.document.querySelector('meta[name="viewport"]');
      if (viewport) {
        // Enable user scaling on mobile for zoom functionality
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=3.0, user-scalable=yes');
      }
      return () => {
        // Restore original viewport settings when leaving mobile approval mode
        if (viewport) {
          viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
          );
        }
      };
    }
  }, [isMobileApprovalMode]);

  // Desktop zoom with Ctrl+scroll wheel for approval mode
  const [zoomLevel, setZoomLevel] = React.useState(1);
  React.useEffect(() => {
    if (!isMobile && isRequestReviewPending && userIsApprover) {
      const handleWheel = (e: WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          const delta = e.deltaY > 0 ? 0.9 : 1.1;
          setZoomLevel((prev) => Math.min(3, Math.max(0.5, prev * delta)));
        }
      };

      window.document.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        window.document.removeEventListener('wheel', handleWheel);
        setZoomLevel(1); // Reset zoom on unmount
      };
    }
  }, [isMobile, isRequestReviewPending, userIsApprover]);

  const dealUrl = paths.deals.detail.replace(':teamId', teamId as string).replace(':dealId', dealId);
  const { possessionTeamName } = useDocumentRole(documentId);
  const sessionActivities = state.sessionActivities;
  const { currentDeal } = useDeal();
  const isDealShared = !!currentDeal?.receiverEmail;
  const { formatMessage } = useIntl();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const { data: documentData, loading: documentLoading } = useQuery(getDocumentById, {
    variables: { documentId: documentId! },
    skip: !documentId,
    fetchPolicy: 'network-only', // Always fetch fresh data when navigating to document
  });

  // DOCX-native compare REPLACES the editor (same pattern as the template page)
  // rather than overlaying it — otherwise the editor stays mounted underneath
  // and its ruler / comments sidebar keep showing.
  const docxEditorEngine = (
    documentData?.getDocumentById?.editorConfiguration as Record<string, any> | null
  )?.editor_engine;
  const isDocxNativeDeal = docxEditorEngine === 'docx_native';
  // DOCX-native version save — the polymorphic mutation the possession-pass /
  // signing / approval triggers use for docx deals (CKE deals keep using the
  // RevisionTracker plugin). Naming is computed by the shared util, so history
  // reads identically across the CKE→docx boundary.
  const [saveDealVersionMutation] = useMutation(mutations.saveDocumentVersion);
  const dealDocxVersionArg = React.useMemo(
    () =>
      isDocxNativeDeal && documentId
        ? {
            documentId: String(documentId),
            saveVersion: (name: string, event: string) =>
              saveDealVersionMutation({
                variables: {
                  documentId: String(documentId),
                  documentType: 'Document',
                  name,
                  event,
                },
              }),
          }
        : undefined,
    [isDocxNativeDeal, documentId, saveDealVersionMutation]
  );

  // Version history for Compare. Fetched in its OWN light query (metadata only,
  // no bodies) so the main document query stays lean; bodies load on demand.
  const { data: revisionsData } = useQuery(queriesV2.getDocumentRevisionsForCompare, {
    variables: { documentId: String(documentId) },
    skip: !isDocxNativeDeal || !documentId,
    fetchPolicy: 'cache-and-network',
  });

  // Loads ONE revision's stored body ({ body, body_storage_format }) on demand
  // from its `data` hash — only when the user actually selects that version.
  const loadDealRevisionBody = React.useCallback(
    async (revisionId: string): Promise<Record<string, any> | null> => {
      const { data } = await apolloClient.query({
        query: queriesV2.getDocumentRevisionBody,
        variables: { documentId: String(documentId) },
        // MUST be network-only. The metadata query normalizes the same
        // `DocumentRevision:id` entities WITHOUT the heavy `data` field, so a
        // cache-first read here hits those cache entries, sees no `data`, and
        // returns null — which surfaced as "could not load one of the versions".
        fetchPolicy: 'network-only',
      });
      const rows = (data?.getDocumentById as Record<string, any> | undefined)?.documentRevisions ?? [];
      const row = rows.find((r: any) => String(r.id) === String(revisionId));
      // Read the TYPED String fields — NOT the `data` JsonType. A large base64
      // body makes the JsonType scalar serialize the whole hash as a bare string
      // (the actual base64, not JSON), so `data.content` was unreachable and
      // Compare showed "could not load one of the versions". snapshotContent is
      // a plain String field that serializes cleanly.
      const body: string | undefined = row?.snapshotContent;
      if (!body) return null;
      return { body, body_storage_format: row?.snapshotFormat ?? 'html' };
    },
    [apolloClient, documentId]
  );

  const [renameVersionMutation] = useMutation(mutations.renameDocumentVersion);
  const handleRenameDealVersion = React.useCallback(
    async (version: { key: string }, name: string) => {
      // key is `revision-<id>`; synthetic versions (current/pre-migration) are
      // filtered out upstream, so this always has a real revision id.
      const revisionId = version.key.replace(/^revision-/, '');
      await renameVersionMutation({ variables: { revisionId, name } });
      await apolloClient.refetchQueries({
        include: [queriesV2.getDocumentRevisionsForCompare],
      });
    },
    [renameVersionMutation, apolloClient]
  );

  const dealCompareVersions = React.useMemo(
    () =>
      isDocxNativeDeal
        ? buildDealCompareVersions(
            (documentData?.getDocumentById as Record<string, any> | undefined)?.document,
            ((revisionsData?.getDocumentById as Record<string, any> | undefined)?.documentRevisions ??
              []).map((r: any) => ({
              id: r.id,
              name: r.name,
              createdAt: r.createdAt,
              authorName: r.creator?.fullName,
              event: r.event,
              hasSnapshot: r.hasSnapshot,
              snapshotFormat: r.snapshotFormat,
            })) as any[],
            loadDealRevisionBody
          )
        : [],
    [isDocxNativeDeal, documentData, revisionsData, loadDealRevisionBody]
  );

  // Sync document permissions to context when loaded
  // This ensures counterparty permissions (form filling, page editing) sync from backend
  useEffect(() => {
    if (documentData?.getDocumentById && !documentLoading) {
      const doc = documentData.getDocumentById;
      updateDocumentState({
        receiverCollaborationEnabled: doc.receiverCollaborationEnabled || false,
        receiverPagePdfEditingEnabled: doc.receiverPagePdfEditingEnabled || false,
      });
    }
  }, [documentData, documentLoading, updateDocumentState]);

  const { data: dealTeamsData } = useQuery(queriesV2.getDealTeamsByTeamId, {
    variables: { dealId },
    skip: !dealId,
  });

  // Revision history hook for proper GraphQL mutations
  const { getDocumentRevisions, updateDocumentRevisions } = useDocumentRevisionHistory(documentId);

  // Redirect PREPARING_TO_SIGN documents to PrepareToSigningContainer
  useEffect(() => {
    if (documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN && documentId && teamId && dealId) {
      const signUrl = fillPlaceholders(paths.Documents.signDocument, { teamId, dealId, documentId });
      navigate(signUrl);
    }
  }, [documentStatus, documentId, teamId, dealId, navigate]);

  // The other party holds the document: it must not open in the EDITOR, but a
  // party to the deal should still be able to re-read what was last shared with
  // them. Previously this bounced them to the deal page, so a document they had
  // just been sent became unreachable until it came back.
  //
  // Now the editor is replaced by a read-only view of the last version SNAPSHOT
  // (never the live body — that is the other side's working copy). Documents
  // whose engine predates version snapshots keep the old redirect, since there
  // is nothing to show them.
  const isExternalReview =
    documentStatus === DocumentStatusEnum.REVIEWING && !hasCurrentTeamPossession;

  // `isDocxNativeDeal` is derived from the document query's editorConfiguration,
  // so it is FALSE for the first render or two while that query is in flight —
  // and this effect fired on that false, bouncing every docx-native document
  // straight back to the deal page before its engine was known. The read-only
  // view was therefore unreachable in practice: the click looked like it did
  // nothing at all (no error, no navigation). Decide only once the query has
  // actually answered.
  const documentEngineResolved = !documentLoading && !!documentData?.getDocumentById;

  useEffect(() => {
    if (!isExternalReview || !teamId || !dealId) return;
    if (!documentEngineResolved) return;
    // Only the docx-native path builds version snapshots; without them there is
    // no read-only view to offer, so preserve the previous behaviour.
    if (isDocxNativeDeal) return;
    navigate(`/teams/${teamId}/deals/${dealId}`);
  }, [isExternalReview, isDocxNativeDeal, documentEngineResolved, teamId, dealId, navigate]);

  const handleModalClose = () => {
    setShowHeaderFooterEditorModal(false);
  };

  const canShare = useMemo(() => {
    const isShareableStatus =
      !documentStatus ||
      [
        DocumentStatusEnum.DRAFT,
        DocumentStatusEnum.REVIEWING,
        DocumentStatusEnum.UPLOADED,
        DocumentStatusEnum.SHARED,
      ].includes(documentStatus);
    return canInvite && isShareableStatus;
  }, [canInvite, documentStatus]);

  const canRequestInternalApproval = useMemo(() => {
    const isShareableStatus =
      !documentStatus ||
      [
        DocumentStatusEnum.DRAFT,
        DocumentStatusEnum.REVIEWING,
        DocumentStatusEnum.UPLOADED,
        DocumentStatusEnum.SHARED,
      ].includes(documentStatus);
    return canEdit && isShareableStatus;
  }, [canEdit, documentStatus]);

  const isUploaded = [DocumentStatusEnum.UPLOADED].includes(documentStatus);

  const documentInReview =
    [DocumentStatusEnum.REVIEWING, DocumentStatusEnum.APPROVAL_PENDING].includes(
      documentStatus as DocumentStatusEnum
    ) ||
    approvalRequest?.status === ApprovalRequestStatus.PENDING ||
    (!isRequestReviewPending && !isEditable);

  const isSignersSidebarAccessible = documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN;

  // Check if user is approver with edit permissions
  const approverHasEditPermissions = userIsApprover && approvalRequest?.permitEditingDuringApproval;

  const isClauseTemplatesSidebarAccessible =
    documentStatus === DocumentStatusEnum.DRAFT ||
    (documentStatus === DocumentStatusEnum.UPLOADED && isDocXDocument) ||
    documentStatus === DocumentStatusEnum.REVIEWING;

  const isDocumentExecuted = documentStatus === DocumentStatusEnum.EXECUTED;
  // Executed docx-native APP-source deal → read-only VERSION HISTORY viewer
  // (list + view a version; comments render natively). PDF-source deals have no
  // editable body and never reach here. Terminated is treated like executed.
  const showDealVersionViewer =
    isDocxNativeDeal &&
    documentSource === DocumentSourceEnum.APP &&
    (documentStatus === DocumentStatusEnum.EXECUTED ||
      documentStatus === DocumentStatusEnum.TERMINATED);
  const hasInvitePermission = !!permissions?.hasInviteSendingPermission;
  const shouldShowUnshareButton =
    isDocumentExecuted &&
    isDocumentCreator &&
    hasInvitePermission &&
    !!documentData?.getDocumentById?.receiverTeam;
  const hasCloseButton =
    isDocumentExecuted || // Always show close button for executed documents
    // Awaiting the counterparty: no editor mounts, so the left sidenav (which
    // is gated on `isEditorLoaded`) never appears either — without this the
    // read-only view is a dead end reachable only by the browser Back button.
    (isExternalReview && isDocxNativeDeal) ||
    (!(isDocumentCreator && documentSource === DocumentSourceEnum.APP && isSignersSidebarAccessible) &&
      !(isClauseTemplatesSidebarAccessible && (isDocumentLoaded || documentSource === DocumentSourceEnum.APP)));

  const handleSendDocumentMutation = useCallback(async () => {
    const isAppDocument = documentSource === DocumentSourceEnum.APP;

    if (isAppDocument) {
      onSave && (await onSave());
      await saveDocumentVersionOnPossessionPass({
        success: true,
        possessionTeamName,
        docx: dealDocxVersionArg,
      });
    }

    const additionalMessage = optionalMessageInputRef.current?.value;

    givePossessionToCounterparty({
      documentId,
      additionalMessage,
      sessionActivities,
    })(apolloClient)
      .then(() => {
        updateDocumentState({ sessionActivities: [] });
        navigate(dealUrl);
        isDealShared &&
          notifySuccess({
            message: formatMessage(
              documentStatus === DocumentStatusEnum.DRAFT ? successSendDocumentMessage : givePossessionSuccessMessage
            ),
          });
      })
      .catch((error) => {
        if (isAppDocument) {
          saveDocumentVersionOnPossessionPass({ success: false });
        }

        notifyError({ message: error.message });
      });
  }, [
    apolloClient,
    dealUrl,
    documentId,
    documentSource,
    documentStatus,
    formatMessage,
    onSave,
    notifyError,
    notifySuccess,
    possessionTeamName,
    givePossessionToCounterparty,
    isDealShared,
    sessionActivities,
    updateDocumentState,
    navigate,
  ]);

  const isReviewing = documentStatus === DocumentStatusEnum.REVIEWING;
  const isDraft = documentStatus === DocumentStatusEnum.DRAFT;
  const isPreparingToSign = documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN;
  const { hasInviteSendingPermission } = useDocumentTeamPermissions({ teamId, apolloClient });

  // Calculate shouldShowInternalSendForSigning same as desktop
  const isEditorLoaded = documentSource === DocumentSourceEnum.APP ? true : true; // For mobile, assume loaded
  const shouldShowInternalSendForSigning =
    isDocumentCreator &&
    permissions?.hasEditPermission &&
    (isDraft || isReviewing || isUploaded || isShared) &&
    hasCurrentTeamPossession &&
    isEditorLoaded;

  // Use same business logic as desktop header
  const {
    shouldShowRequestReviewContainer,
    shouldShowSendForSigning,
    shouldShowInternalSendForSigning: shouldShowInternalSendForSigningFromHook,
  } = useSendDropdownLogic({
    isDocumentCreator,
    hasCurrentTeamPossession,
    canShare,
    canEdit,
    canRequestInternalApproval,
    isReviewing,
    isDocumentEditable: isEditable,
    documentSource,
    approvalRequest,
    shouldShowInternalSendForSigning,
    isPreparingToSign,
    isExecuted: isDocumentExecuted,
  });

  // Unshare handlers for mobile
  const handleUnsendModalClose = () => {
    setIsUnsendModalOpen(false);
  };

  const handleUnsendConfirm = async () => {
    setIsUnsendLoading(true);
    try {
      await unsendDocument({ documentId, dealId })(apolloClient);
      // Refetch queries to update UI with latest data
      await apolloClient.refetchQueries({
        include: ['GetDocumentById', 'GetDocumentsByDeal'],
      });
      // Show success notification
      setTimeout(() => {
        notifySuccess({
          message: formatMessage({
            id: 'unsend-document.success',
            defaultMessage: 'Document unshared successfully. Counterparty access has been removed.',
          }),
        });
      }, 100);
      setIsUnsendModalOpen(false);
    } catch (error) {
      console.error('Error unsending document:', error);
      notifyError({
        message: formatMessage({
          id: 'unsend-document.error',
          defaultMessage: 'Failed to unshare document. Please try again.',
        }),
      });
    } finally {
      setIsUnsendLoading(false);
    }
  };

  return (
    <div
      className={classNames('document-wizard-page', {
        'document-editor--approval': isApprovalMode,
        'document-editor--mobile': isMobile,
      })}
    >
      <Helmet
        title={formatMessage(
          {
            id: 'title.document-editor',
            defaultMessage: `${documentTitle}`,
          },
          { title: documentTitle }
        )}
      />
      <div>
        {isDocumentCreator &&
          documentSource === DocumentSourceEnum.APP &&
          isSignersSidebarAccessible &&
          !isApprovalMode &&
          !approverHasEditPermissions &&
          !isDocumentExecuted && (
            <LeaseWizardSideNav isCounterparty={!isDocumentCreator} onSave={onSave}>
              <aside
                className={classNames('document-wizard-page__sidebar-layout', {
                  'document-wizard-page__sidebar-layout-hidden': !isDocumentCreator,
                })}
              >
                <SignersSidebar permissions={permissions} />
              </aside>
            </LeaseWizardSideNav>
          )}
        {isClauseTemplatesSidebarAccessible &&
          isDocumentLoaded &&
          !isApprovalMode &&
          !(isMobile && isRequestReviewPending && userIsCreator) &&
          !approverHasEditPermissions && (
            <DocumentActivitiesAndTemplatesSidenav
              permissions={permissions}
              isDocumentCreator={isDocumentCreator}
              isDocumentRestricted={isDocumentRestricted}
              isCollapsible={!isCompareModeEnabled}
              onClose={handleClose}
              handleSendDocumentMutation={handleSendDocumentMutation}
              canEdit={canEdit}
              isReviewing={isReviewing}
              hasInviteSendingPermission={hasInviteSendingPermission}
              dealId={dealId}
              teamId={teamId}
              documentId={documentId}
              optionalMessageInputRef={optionalMessageInputRef}
              onSave={onSave}
              canShare={canShare}
              isDraft={isDraft}
              isShared={isShared}
              isUploaded={isUploaded}
            />
          )}
      </div>

      <div className="document-wizard-page__full-size-layout">
        <div className="document-wizard-page__header-layout">
          {isMobileApprovalMode ? (
            <MobileApprovalHeader
              onClose={handleClose}
              title={documentTitle}
              actions={
                <MobileApprovalActionsDropdown
                  documentId={documentId}
                  dealId={dealId}
                  teamId={teamId}
                  reviewId={approvalRequest?.reviews?.[0]?.id || ''}
                  handleSaveDocument={onSave}
                />
              }
            />
          ) : isMobileCreatorPendingMode ? (
            <MobileCreatorPendingHeader
              onClose={handleClose}
              title={documentTitle}
              documentId={documentId}
              dealId={dealId}
              teamId={teamId}
              requestId={approvalRequest?.id || ''}
              approvalRequest={approvalRequest}
            />
          ) : isMobileNormalMode ? (
            <MobileDocumentHeader
              onClose={hasCloseButton ? handleClose : undefined}
              title={documentTitle}
              titleBadge={<DocumentBadgeContainer />}
              onChatClick={() => setIsChatOpen(true)}
              showUnshareButton={shouldShowUnshareButton}
              onUnshareClick={() => setIsUnsendModalOpen(true)}
              showDownloadButton={isDocumentExecuted}
              onDownloadClick={() => {
                // Download signed PDF
                const signedPdfUrl = documentData?.getDocumentById?.signedPdfUrl;
                const eversignPdfUrl = documentData?.getDocumentById?.eversignPdfUrl;
                const pdfUrl = signedPdfUrl || eversignPdfUrl;
                if (pdfUrl) {
                  // Simple download using anchor element
                  const link = window.document.createElement('a');
                  link.href = pdfUrl;
                  link.download = `${documentTitle}.pdf`;
                  link.click();
                }
              }}
              sendDropdown={
                // Only show sendDropdown if NOT executed (executed docs show icon buttons instead)
                !isDocumentExecuted && (
                  isSigning ? (
                    <ExecutePageActions
                      documentId={documentId}
                      dealId={dealId}
                      teamId={teamId}
                      documentTitle={documentTitle}
                      hasEditPermission={permissions?.hasEditPermission ?? false}
                    />
                  ) : shouldShowRequestReviewContainer && dealTeamsData ? (
                    <RequestReviewContainer
                      dealTeamsData={dealTeamsData}
                      documentTeamsAdminData={documentData}
                      documentId={documentId}
                      dealId={dealId}
                      handleSaveDocument={onSave}
                      teamId={teamId}
                      isDocumentEditable={isEditable}
                      isDocumentCreator={isDocumentCreator}
                      isDocumentShared={isShared}
                      shouldShowInternalSendForSigning={shouldShowInternalSendForSigningFromHook}
                      hasInviteSendingPermission={hasInviteSendingPermission}
                      hasEditPermission={permissions?.hasEditPermission ?? false}
                    />
                  ) : null
                )
              }
            />
          ) : (
            <DocumentWizardHeaderContainer
              canEdit={canEdit}
              isViewingCompare={isDocxNativeDeal && docxViewCompare}
              onExitCompare={() => setDocxViewCompare(false)}
              onClose={
                // Always show X button for approvers with edit permissions (square blue style)
                isApprovalMode || hasCloseButton || approverHasEditPermissions ? handleClose : undefined
              }
              title={documentTitle}
              titleBadge={<DocumentBadgeContainer />}
              lastSaved={
                <DocumentWizardHeaderLastSaved
                  datetime={
                    lastSavedLocal && (!lastUpdated || lastSavedLocal > lastUpdated)
                      ? lastSavedLocal
                      : lastUpdated
                  }
                  intervalInSecond={60}
                />
              }
              onSaveTitle={onTitleSave}
              canShare={canShare} // Let existing header handle share visibility
              canRequestInternalApproval={canRequestInternalApproval} // Let existing header handle approval visibility
              permissions={permissions} // Use original permissions for header (cancel button, approval actions)
              onSave={onSave}
              documentId={documentId}
              dealId={dealId}
              teamId={teamId}
              documentSource={documentSource}
              isApprovalMode={isApprovalMode}
            />
          )}
          {!documentLoading && (isUploaded || isDocumentExecuted) && !isDocXDocument && hasInvitePermission && !isRequestReviewPending && !documentData?.getDocumentById?.receiverTeam && (
            <DocumentSubheaderContainer documentId={documentId} dealId={dealId} teamId={teamId} grantAccessSubheader />
          )}
          {isRequestReviewApproved && !isDocumentExecuted && (
            <DocumentSubheaderContainer
              documentId={documentId}
              dealId={dealId}
              teamId={teamId}
              unlockDocumentSubheader
            />
          )}
          {/* Approval permission banners */}
          {approverHasEditPermissions && isRequestReviewPending && (
            <div className="document-wizard-page__approval-banner document-wizard-subheader-info">
              <nav className="document-wizard-header__actions-subheader">
                <div className="document-wizard-subheader__document-title-container">
                  <span>You can make changes and add comments during the approval process</span>
                </div>
              </nav>
            </div>
          )}
          {userIsApprover && !approverHasEditPermissions && isRequestReviewPending && (
            <div className="document-wizard-page__approval-banner document-wizard-subheader-info">
              <nav className="document-wizard-header__actions-subheader">
                <div className="document-wizard-subheader__document-title-container">
                  <span>You're able to add comments during the approval process</span>
                </div>
              </nav>
            </div>
          )}
        </div>
        <div
          className="document-wizard-page__content-wrapper"
          style={
            !isMobile && zoomLevel !== 1
              ? {
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'top left',
                  width: `${100 / zoomLevel}%`,
                }
              : undefined
          }
        >
          {isDocxNativeDeal && docxViewCompare && (
            // Compare OVERLAYS the editor rather than replacing it. Unmounting
            // the editor cleared DocumentContext.isEditorLoaded, which is what
            // gates the left sidenav and the Send dropdown — so a full swap made
            // the whole page chrome vanish and felt disjointed. Keeping the
            // editor mounted (hidden) preserves that state and makes returning
            // to editing instant.
            <div className="document-wizard-page__compare-overlay">
              <CompareRedlineView
                versions={dealCompareVersions}
                onRenameVersion={handleRenameDealVersion}
              />
            </div>
          )}
          {isExternalReview && isDocxNativeDeal ? (
            // The other party holds the document — show the last version shared
            // with this team, read-only, instead of locking them out entirely.
            <div className="document-wizard-page__editor-slot">
              <AwaitingCounterpartyView versions={dealCompareVersions} />
            </div>
          ) : showDealVersionViewer ? (
            // Read-only version history for the executed/final deal doc.
            <div className="document-wizard-page__editor-slot">
              <DealVersionViewer versions={dealCompareVersions} />
            </div>
          ) : (documentSource === DocumentSourceEnum.APP || isDocXDocument) && (
            // Hidden (not unmounted) while comparing — see the overlay note above.
            <div
              className="document-wizard-page__editor-slot"
              style={isDocxNativeDeal && docxViewCompare ? { display: 'none' } : undefined}
            >
            <CommentContextProvider InitialComments={[]}>
              <DocumentEditorSwitch
                onSaved={handleDocxSaved}
                onCompareVersions={() => setDocxViewCompare(true)}
                // V4.0: route to the DOCX-native deal editor when this document
                // is flagged; every other value falls through to CKEditor.
                editorEngine={
                  (documentData?.getDocumentById?.editorConfiguration as Record<string, any> | null)
                    ?.editor_engine
                }
                documentId={documentId}
                documentType="offer"
                save={onSave}
                document={document}
                teamId={teamId}
                showMenu={
                  // Show menu for creators when not in approval mode
                  (isDocumentCreator && !isApprovalMode) ||
                  // Also show menu for approvers with editing permissions
                  (userIsApprover && approvalRequest?.permitEditingDuringApproval)
                }
                hasCurrentTeamPossession={hasCurrentTeamPossession}
                canEdit={isApprovalMode ? false : canEdit}
                canComment={isApprovalMode ? true : canComment}
                permissions={mobileEditorPermissions} // Use restricted permissions for editor
                approvalRequest={approvalRequest}
                documentData={documentData}
                documentLoading={documentLoading}
                editorMode={isCompareModeEnabled ? 'compare' : 'edit'}
              />
            </CommentContextProvider>
            </div>
          )}
          {documentSource === DocumentSourceEnum.IMPORTED && !isDocXDocument && (
            <UploadedDocumentsViewer
              documentId={documentId}
              teamId={teamId}
              userPermissions={permissions}
              hasCurrentTeamPossession={hasCurrentTeamPossession}
              isDocumentInReview={documentInReview}
            />
          )}
        </div>
      </div>
      {/* The separate header/footer editor modal was a CKEditor-era affordance;
          the DOCX-native editor edits headers and footers inline. Nothing ever
          opened it (`showHeaderFooterEditorModal` was only ever set false), so
          it went with CKEditor. */}
      {/* Render AI Chat for all modes (mobile and desktop) */}
      <AiChatSlideout
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        documentId={documentId}
        documentName={documentTitle}
        teamId={teamId}
        dealId={dealId}
      />
      {/* Unshare confirmation modal for mobile */}
      <ConfirmActionModal
        show={isUnsendModalOpen}
        onHide={handleUnsendModalClose}
        onClick={handleUnsendConfirm}
        title={<FormattedMessage id="unsend-document.modal.title" defaultMessage="Unshare Document" />}
        message={
          <FormattedMessage
            id="unsend-document.modal.message"
            defaultMessage="Are you sure you want to unshare this document? The counterparty will lose access and the document will return to your possession."
          />
        }
        action={
          isUnsendLoading ? (
            <FormattedMessage id="unsend-document.modal.action.loading" defaultMessage="Unsharing..." />
          ) : (
            <FormattedMessage id="unsend-document.modal.action" defaultMessage="Unshare" />
          )
        }
      />
    </div>
  );
};

export default DocumentPage;
